import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, getCurrentUser } from '../../redux/slices/authSlice';
import { useForm } from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { useAuthPageGuard } from '../../hooks/useAuthPageGuard';
import alertify from 'alertifyjs';
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  CircularProgress
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import PasswordField from '../../components/common/PasswordField';

const LoginPage = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  // 🔐 Auth page guard - handles redirect, errors, and cleanup
  // Replaces 3 duplicate useEffects with 1 reusable hook
  useAuthPageGuard();

  // Form validation
  const { formData, errors, handleChange, validate } = useForm(
    { email: '', password: '' },
    {
      email: validators.email,
      password: (val) => validators.required(val, 'Şifre')
    }
  );

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const isValid = validate();
    if (!isValid) {
      alertify.error('Lütfen formu eksiksiz doldurun');
      return;
    }

    // Dispatch login action
    const resultAction = await dispatch(login(formData));
    
    // If login successful, fetch menu and permissions immediately
    // Phase 5: This ensures menu loads without requiring page refresh
    if (login.fulfilled.match(resultAction)) {
      dispatch(getCurrentUser());
    }
  };

  return (
    <Box
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100"
      sx={{ py: 4 }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          {/* Header */}
          <Box className="text-center mb-6">
            <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Giriş Yap
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hesabınıza giriş yapın
            </Typography>
          </Box>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              disabled={loading}
              autoComplete="email"
              autoFocus
            />

            {/* Password Field */}
            <PasswordField
              label="Şifre"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              disabled={loading}
              autoComplete="current-password"
            />

            {/* Forgot Password Link */}
            <Box className="text-right mt-2 mb-4">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Şifremi Unuttum
              </Link>
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
              sx={{ mt: 2, mb: 2, py: 1.5 }}
            >
              {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
            </Button>
          </form>

          {/* Register Link */}
          <Box className="text-center mt-4">
            <Typography variant="body2" color="text.secondary">
              Hesabınız yok mu?{' '}
              <Link
                to="/register"
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                Kayıt Ol
              </Link>
            </Typography>
          </Box>
        </Paper>

        {/* Back to Home Link */}
        <Box className="text-center mt-4">
          <Link
            to="/"
            className="text-sm text-gray-600 hover:text-gray-800 hover:underline"
          >
            ← Ana Sayfaya Dön
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;

